import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Trash2, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';

import type { PhotoListItem } from '@/api/types/photo.types';
import { getPhotoThumbnailUrl } from '@/api/services/photo.service';
import { useBulkAction } from '@/api/hooks/usePhotos';

interface TimelineViewProps {
  photos: PhotoListItem[];
  onPhotoClick: (photoId: string) => void;
  eventId?: string;
}

const OUTLIER_GAP_HOURS = 5;

interface TimelineGroup {
  label: string;
  startTime: Date;
  endTime: Date;
  photos: PhotoListItem[];
  isOutlier: boolean;
}

function groupPhotosByTimeline(photos: PhotoListItem[]): TimelineGroup[] {
  const withTime = photos
    .filter((p) => p.taken_at)
    .sort((a, b) => new Date(a.taken_at!).getTime() - new Date(b.taken_at!).getTime());

  if (withTime.length === 0) return [];

  const groups: TimelineGroup[] = [];
  let currentGroup: PhotoListItem[] = [withTime[0]];
  let groupStart = new Date(withTime[0].taken_at!);

  for (let i = 1; i < withTime.length; i++) {
    const prevTime = new Date(withTime[i - 1].taken_at!);
    const currTime = new Date(withTime[i].taken_at!);
    const gapHours = (currTime.getTime() - prevTime.getTime()) / (1000 * 60 * 60);

    if (gapHours > OUTLIER_GAP_HOURS) {
      // Close current group
      groups.push({
        label: format(groupStart, 'dd/MM/yyyy HH:mm'),
        startTime: groupStart,
        endTime: prevTime,
        photos: currentGroup,
        isOutlier: false,
      });
      // Start new group
      currentGroup = [withTime[i]];
      groupStart = currTime;
    } else {
      currentGroup.push(withTime[i]);
    }
  }

  // Close last group
  if (currentGroup.length > 0) {
    groups.push({
      label: format(groupStart, 'dd/MM/yyyy HH:mm'),
      startTime: groupStart,
      endTime: new Date(currentGroup[currentGroup.length - 1].taken_at!),
      photos: currentGroup,
      isOutlier: false,
    });
  }

  // Find the main group (largest) and mark others as outliers
  if (groups.length > 1) {
    let maxIdx = 0;
    for (let i = 1; i < groups.length; i++) {
      if (groups[i].photos.length > groups[maxIdx].photos.length) {
        maxIdx = i;
      }
    }
    for (let i = 0; i < groups.length; i++) {
      if (i !== maxIdx) {
        groups[i].isOutlier = true;
      }
    }
  }

  return groups;
}

function formatDuration(ms: number): string {
  const totalMins = Math.floor(ms / 60000);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hours > 0) return hours + "h " + mins + "m";
  return mins + "m";
}

function formatRelativeTime(photo: PhotoListItem, eventStart: Date): string {
  if (!photo.taken_at) return "";
  const diff = new Date(photo.taken_at).getTime() - eventStart.getTime();
  return "+" + formatDuration(diff);
}

export const TimelineView = React.memo(function TimelineView({
  photos,
  onPhotoClick,
}: TimelineViewProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupPhotosByTimeline(photos), [photos]);
  const bulkMutation = useBulkAction();
  const [collapsedOutliers, setCollapsedOutliers] = useState<Set<number>>(new Set());

  const mainGroup = groups.find((g) => !g.isOutlier);
  const eventStart = mainGroup?.startTime ?? (groups[0]?.startTime ?? new Date());

  const toggleCollapse = useCallback((idx: number) => {
    setCollapsedOutliers((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const handleDeleteGroup = useCallback((group: TimelineGroup) => {
    if (!confirm(t('gallery.timeline.confirmDelete', { count: group.photos.length }))) return;
    bulkMutation.mutate({
      action: 'delete',
      photo_ids: group.photos.map((p) => p.id),
    });
  }, [bulkMutation, t]);

  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Clock className="h-12 w-12 text-gray-300 dark:text-gray-600" />
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          {t('gallery.timeline.noTimestamps')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map((group, idx) => (
        <div
          key={idx}
          className={
            "rounded-xl border " +
            (group.isOutlier
              ? "border-amber-300 bg-amber-50/50 dark:border-amber-700 dark:bg-amber-950/20"
              : "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900")
          }
        >
          {/* Group header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-3">
              {group.isOutlier && (
                <AlertTriangle className="h-5 w-5 text-amber-500" />
              )}
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {group.label}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {group.photos.length} {t('gallery.photos')}
                  {" · "}
                  {formatDuration(group.endTime.getTime() - group.startTime.getTime())}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {group.isOutlier && (
                <>
                  <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                    {t('gallery.timeline.outlier')}
                  </span>
                  <button
                    onClick={() => handleDeleteGroup(group)}
                    className="btn-danger inline-flex items-center gap-1.5 text-xs px-2.5 py-1"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {t('gallery.timeline.deleteGroup')}
                  </button>
                </>
              )}
              <button
                onClick={() => toggleCollapse(idx)}
                className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                {collapsedOutliers.has(idx) ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronUp className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {/* Photos in timeline */}
          {!collapsedOutliers.has(idx) && (
            <div className="p-4">
              {/* Timeline bar */}
              <div className="relative">
                <div className="absolute left-[70px] top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />
                <div className="space-y-2">
                  {group.photos.map((photo) => (
                    <div
                      key={photo.id}
                      className="flex items-center gap-3 cursor-pointer group"
                      onClick={() => onPhotoClick(photo.id)}
                    >
                      <span className="w-[60px] shrink-0 text-right text-[11px] font-mono text-gray-500 dark:text-gray-400">
                        {formatRelativeTime(photo, eventStart)}
                      </span>
                      <div className="relative z-10 h-2.5 w-2.5 shrink-0 rounded-full border-2 border-primary-500 bg-white dark:bg-gray-900" />
                      <div className="flex items-center gap-2.5 flex-1 min-w-0 rounded-lg p-1.5 -m-1.5 transition-colors group-hover:bg-gray-50 dark:group-hover:bg-gray-800/50">
                        <img
                          src={getPhotoThumbnailUrl(photo.id, 'small')}
                          alt={photo.original_filename}
                          className="h-10 w-10 shrink-0 rounded object-cover"
                          loading="lazy"
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium text-gray-800 dark:text-gray-200">
                            {photo.original_filename}
                          </p>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500">
                            {photo.taken_at ? format(parseISO(photo.taken_at), 'HH:mm:ss') : ''}
                            {photo.quality_score != null && (" · Q:" + Math.round(photo.quality_score))}
                            {photo.face_count > 0 && (" · " + photo.face_count + " faces")}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
});
